import {
  Drawer,
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  IconButton,
  Button,
  LinearProgress,
  Chip,
} from "@mui/material";
import LanguageIcon from "@mui/icons-material/Language";
import LogoutIcon from "@mui/icons-material/Logout";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import BugReportIcon from "@mui/icons-material/BugReport";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@context/AuthContext";
import { useThemeMode } from "@context/ThemeContext";
import { useAppSelector } from "@/store";
import { DRAWER_WIDTH, NAV_ITEMS } from "./constants";
import { SIDEBAR_BACKGROUND } from "@/constants/layout";

interface SidebarProps {
  mobileOpen: boolean;
  onMobileClose: () => void;
}

export function Sidebar({ mobileOpen, onMobileClose }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { isAuthenticated, logout } = useAuth();
  const { mode, toggleTheme } = useThemeMode();
  const stats = useAppSelector((s) => s.scrapers.stats);

  const visibleItems = NAV_ITEMS.filter(
    (item) => !item.requiresAuth || isAuthenticated,
  );

  const handleNav = (path: string) => {
    navigate(path);
    onMobileClose();
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
    onMobileClose();
  };

  const toggleLang = () => {
    i18n.changeLanguage(i18n.language === "en" ? "hr" : "en");
  };

  const sidebarBg =
    mode === "light" ? SIDEBAR_BACKGROUND.light : SIDEBAR_BACKGROUND.dark;

  const drawerContent = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: sidebarBg,
        color: "#e2e8f0",
      }}
    >
      <Box
        sx={{
          p: 3,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          cursor: "pointer",
        }}
        onClick={() => handleNav("/")}
      >
        <BugReportIcon sx={{ fontSize: 32, color: "#a78bfa" }} />
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: "bold",
              lineHeight: 1,
              color: "#fff",
            }}
          >
            {t("app.name")}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              textTransform: "uppercase",
              letterSpacing: 1.5,
              fontSize: 10,
              color: "#94a3b8",
            }}
          >
            {t("app.subtitle")}
          </Typography>
        </Box>
      </Box>

      <List sx={{ flex: 1, px: 1.5 }}>
        {visibleItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <ListItemButton
              key={item.path}
              selected={isActive}
              onClick={() => handleNav(item.path)}
              sx={{
                mb: 0.5,
                borderRadius: 2,
                color: isActive ? "#a78bfa" : "#cbd5e1",
                "&.Mui-selected": {
                  bgcolor: "rgba(167, 139, 250, 0.12)",
                  color: "#a78bfa",
                  "&:hover": { bgcolor: "rgba(167, 139, 250, 0.16)" },
                },
                "&:hover": { bgcolor: "rgba(255,255,255,0.05)" },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <item.icon sx={{ color: isActive ? "#a78bfa" : "#94a3b8" }} />
              </ListItemIcon>
              <ListItemText
                primary={t(item.i18nKey)}
                slotProps={{
                  primary: { sx: { fontSize: 14, fontWeight: 500 } },
                }}
              />
              {item.showBadge && stats && (
                <Chip
                  label={`${stats.totalScrapers}/${stats.maxScrapers}`}
                  size="small"
                  sx={{
                    bgcolor: "rgba(167, 139, 250, 0.2)",
                    color: "#a78bfa",
                    fontSize: 11,
                    height: 22,
                  }}
                />
              )}
            </ListItemButton>
          );
        })}
        <ListItemButton
          component="a"
          href="https://bugs.pinehood.tech/"
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            mb: 0.5,
            borderRadius: 2,
            color: "#cbd5e1",
            "&:hover": { bgcolor: "rgba(255,255,255,0.05)" },
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <BugReportIcon sx={{ color: "#94a3b8" }} />
          </ListItemIcon>
          <ListItemText
            primary={t("nav.reportBug")}
            slotProps={{ primary: { sx: { fontSize: 14, fontWeight: 500 } } }}
          />
        </ListItemButton>
      </List>

      {isAuthenticated && stats && (
        <>
          <Box sx={{ px: 2.5, pb: 1.5 }}>
            <Typography
              variant="caption"
              sx={{
                color: "#94a3b8",
                fontSize: 11,
              }}
            >
              {t("scrapers.usage", {
                current: stats.totalScrapers,
                max: stats.maxScrapers,
              })}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={(stats.totalScrapers / stats.maxScrapers) * 100}
              sx={{
                mt: 0.5,
                height: 6,
                borderRadius: 3,
                bgcolor: "rgba(255,255,255,0.1)",
                "& .MuiLinearProgress-bar": {
                  bgcolor: "#a78bfa",
                  borderRadius: 3,
                },
              }}
            />
          </Box>
        </>
      )}

      <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
        }}
      >
        <IconButton
          onClick={toggleTheme}
          size="small"
          sx={{ color: "#94a3b8" }}
        >
          {mode === "dark" ? <Brightness7Icon /> : <Brightness4Icon />}
        </IconButton>
        <Button
          size="small"
          onClick={toggleLang}
          sx={{ minWidth: 40, fontWeight: 600, color: "#94a3b8" }}
          startIcon={<LanguageIcon />}
        >
          {i18n.language === "en" ? "HR" : "EN"}
        </Button>
      </Box>

      {isAuthenticated && (
        <>
          <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />
          <Box sx={{ p: 1.5 }}>
            <ListItemButton
              onClick={handleLogout}
              sx={{
                borderRadius: 2,
                color: "#f87171",
                "&:hover": { bgcolor: "rgba(248,113,113,0.1)" },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <LogoutIcon sx={{ color: "#f87171" }} />
              </ListItemIcon>
              <ListItemText
                primary={t("nav.signOut")}
                slotProps={{
                  primary: { sx: { fontSize: 14, fontWeight: 500 } },
                }}
              />
            </ListItemButton>
          </Box>
        </>
      )}
    </Box>
  );

  return (
    <>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", lg: "none" },
          "& .MuiDrawer-paper": {
            width: DRAWER_WIDTH,
            border: "none",
          },
        }}
      >
        {drawerContent}
      </Drawer>

      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", lg: "block" },
          "& .MuiDrawer-paper": {
            width: DRAWER_WIDTH,
            border: "none",
          },
        }}
        open
      >
        {drawerContent}
      </Drawer>
    </>
  );
}
